import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Nav,
  NavLink,
  NavItem,
  TabContent,
  TabPane,
  Label,
  Input,
  Button,
  Spinner,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';

const fieldMapping = {
  procurementNumber: 'procurementNumber',
  titleProcurement: 'titleProcurement',
  procurementType: 'procurementType',
  productType: 'productType',
  description: 'description',
  statusInvitationWording: 'statusInvitationWording',
};

const TABS = [
  {
    id: 'product-hydro',
    label: 'Product Hydro',
    icon: 'ri-drop-line',
    procurementTypeHeader: '1',
  },
  {
    id: 'hydrocarbon-condensat',
    label: 'Hydrocarbon & Condensate',
    icon: 'ri-test-tube-line',
    procurementTypeHeader: '2',
  },
];

const procurementTypeMap = {
  '': '-',
  PelelanganKhusus: 'Pelelangan Khusus',
  PemilihanLangsung: 'Pemilihan Langsung',
  TunjukLangsung: 'Tunjuk Langsung',
};

const INVITATION_STATUS_META = {
  Open: { color: 'primary', label: 'Open' },
  Accepted: { color: 'success', label: 'Accepted' },
  Declined: { color: 'danger', label: 'Declined' },
};

const StatusPill = ({ wording, metaMap, fallbackColor = 'secondary' }) => {
  if (!wording) return <span className='text-muted'>-</span>;

  const meta = metaMap[wording] || {};
  const color = meta.color || fallbackColor;
  const label = meta.label || wording;

  return (
    <Badge
      color={color}
      className='d-inline-block px-2 py-1 fs-12 fw-medium rounded-pill text-wrap text-start'
      style={{
        lineHeight: 1.35,
        maxWidth: '100%',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}
      title={wording}
    >
      {label}
    </Badge>
  );
};

const TABLE_CUSTOM_STYLES = {
  header: {
    style: {
      backgroundColor: '#f0f0f0',
      color: '#333',
      fontWeight: 'bold',
      minHeight: '40px',
    },
  },
  headCells: {
    style: {
      backgroundColor: '#f0f0f0',
      fontWeight: 'bold',
      fontSize: '14px',
      paddingLeft: '8px',
      paddingRight: '8px',
    },
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      wordBreak: 'break-word',
      paddingTop: '4px',
      paddingBottom: '4px',
    },
  },
  rows: { style: { minHeight: '45px' } },
  table: { style: { width: '100%' } },
  tableWrapper: { style: { display: 'table' } },
};

const InvitationView = () => {
  const navigate = useNavigate();
  const token = getAuthToken();
  const [data, setData] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('product-hydro');
  const [sortField, setSortField] = useState('procurementNumber');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const columns = [
    {
      id: 'procurementNumber',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>ID Proc</span>
      ),
      selector: row => row.procurementNumber,
      cell: row => <span className='text-dark'>{row.procurementNumber}</span>,
      width: '110px',
      sortable: true,
    },
    {
      id: 'titleProcurement',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Title</span>
      ),
      selector: row => row.titleProcurement,
      cell: row => (
        <span className='text-dark text-truncate' title={row.titleProcurement}>
          {row.titleProcurement}
        </span>
      ),
      minWidth: '220px',
      sortable: true,
    },
    {
      id: 'procurementType',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Proc Type
        </span>
      ),
      selector: row => row.procurementType,
      cell: row => (
        <span className='text-dark'>
          {procurementTypeMap[row.procurementType] ||
            row.procurementType ||
            '-'}
        </span>
      ),
      width: '180px',
      sortable: true,
    },
    {
      id: 'productType',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Product Type
        </span>
      ),
      selector: row => row.productType?.product?.name,
      cell: row => (
        <div className='d-flex flex-column justify-content-center py-2'>
          <span className='fw-medium text-dark'>
            {row.productType?.product?.name}
          </span>
          <span className='text-muted small' style={{ fontSize: '11px' }}>
            {row.productType?.name}
          </span>
        </div>
      ),
      minWidth: '200px',
      sortable: true,
    },
    {
      id: 'description',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Description
        </span>
      ),
      selector: row => row.description,
      cell: row => (
        <span className='text-dark text-truncate' title={row.description}>
          {row.description}
        </span>
      ),
      minWidth: '220px',
      sortable: true,
    },
    {
      id: 'statusInvitationWording',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Status</span>
      ),
      selector: row => row.statusInvitationWording,
      cell: row => (
        <StatusPill
          wording={row.statusInvitationWording}
          metaMap={INVITATION_STATUS_META}
        />
      ),
      width: '140px',
      sortable: true,
      wrap: true,
    },
    {
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted text-center w-100'>
          Action
        </span>
      ),
      cell: row => (
        <div className='text-center w-100'>
          <button
            className='btn btn-soft-primary btn-sm btn-icon'
            onClick={() => navigate(`../apps-invitation/dtl/${row.id}`)}
            title='Detail'
          >
            <i className='ri-eye-line align-bottom'></i>
          </button>
        </div>
      ),
      width: '90px',
      center: true,
      sortable: false,
    },
  ];

  const MobileCard = ({ item }) => (
    <div className='card mb-3 d-md-none border shadow-none'>
      <div className='card-body p-3'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <span className='text-primary fw-semibold fs-13'>
            {item.procurementNumber}
          </span>
          <StatusPill
            wording={item.statusInvitationWording}
            metaMap={INVITATION_STATUS_META}
          />
        </div>
        <h6 className='card-title fw-bold mb-1 text-dark lh-base'>
          {item.titleProcurement}
        </h6>
        <p className='text-muted fs-12 mb-3'>
          {item.productType?.product?.name} <span className='mx-1'>•</span>{' '}
          {item.productType?.name}
        </p>
        <Button
          color='light'
          size='sm'
          className='w-100 border bg-white shadow-none text-dark fw-medium py-2'
          onClick={() => navigate(`../apps-invitation/dtl/${item.id}`)}
        >
          <i className='ri-eye-line align-bottom me-1 text-muted'></i> View
          Detail
        </Button>
      </div>
    </div>
  );

  const fetchData = async (page, pageSize, authToken) => {
    const activeTabData = TABS.find(tab => tab.id === activeTab);
    const pth = activeTabData?.procurementTypeHeader || '';
    setIsLoading(true);
    try {
      let url = `${api.API_URL}/dmut/get-invitation?PageNumber=${page}&PageSize=${pageSize}&ProcurementTypeHeader=${pth}`;

      if (sortField) {
        url += `&SortBy=${sortField}`;
        const isDescending = sortDirection === 'desc' ? 'true' : 'false';
        url += `&IsDescending=${isDescending}`;
      }

      if (searchText) {
        url += `&Title=${encodeURIComponent(searchText)}`;
      }

      if (statusFilter) {
        url += `&Status=${encodeURIComponent(statusFilter)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        credentials: 'same-origin',
      });
      const result = await response.json();
      setData({ items: result.data.items || [] });
      setTotalRows(result.data.totalItems || 0);
    } catch {
      toast.error('Failed to load invitation data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    rowsPerPage,
    token,
    activeTab,
    sortField,
    sortDirection,
    searchText,
    statusFilter,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchText, statusFilter]);

  const handleSearch = useCallback(e => {
    setSearchText(e.target.value);
  }, []);

  const handleStatusFilter = useCallback(value => {
    setStatusFilter(value);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchText('');
    setStatusFilter('');
  }, []);

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((column, direction) => {
    const sortFieldName =
      fieldMapping[column.id] || column.id || 'procurementNumber';
    setSortField(sortFieldName);
    setSortDirection(direction);
  }, []);

  document.title = 'List Invitation';

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <div className='email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4 pb-0'>
                <div className='border-bottom border-bottom-dashed'>
                  <Row className='mt-n2 mb-1 mb-sm-0'>
                    <div className='col-sm order-3 order-sm-2'>
                      <div className='hstack gap-sm-1 align-items-center flex-wrap email-topbar-link'>
                        <h5 className='card-title mb-0 flex-grow-1'>
                          <Breadcrumb listClassName='p-3 py-2 bg-light'>
                            <BreadcrumbItem>
                              <Link to='/dashboard'>Home</Link>
                            </BreadcrumbItem>
                            <BreadcrumbItem>
                              <Link to='/apps-invitation/view'>Invitation</Link>
                            </BreadcrumbItem>
                            <BreadcrumbItem active>View</BreadcrumbItem>
                          </Breadcrumb>
                        </h5>
                      </div>
                    </div>
                  </Row>

                  <Row className='align-items-end mt-1 mb-1'>
                    <Col xs={12}>
                      <CardHeader className='border-0'>
                        <h5 className='card-title mb-0 text-uppercase'>
                          Invitation
                        </h5>
                      </CardHeader>
                    </Col>
                  </Row>
                </div>

                <div className='message-list-content mx-n4 px-4 message-list-scroll'>
                  <Nav tabs className='nav-tabs-custom nav-primary mt-3 mb-0'>
                    {TABS.map(tab => (
                      <NavItem key={tab.id}>
                        <NavLink
                          className={classnames('cursor-pointer', {
                            active: activeTab === tab.id,
                          })}
                          onClick={() => setActiveTab(tab.id)}
                        >
                          <i className={`${tab.icon} align-middle me-1`}></i>
                          <span className='fw-semibold'>{tab.label}</span>
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  <TabContent activeTab={activeTab} className='pt-3'>
                    {TABS.map(tab => (
                      <TabPane key={tab.id} tabId={tab.id}>
                        <Row>
                          <Col lg={12}>
                            <Card id='InvitationList'>
                              <CardHeader className='border-0'>
                                <div className='d-flex align-items-center'>
                                  <h5 className='card-title mb-0 flex-grow-1'>
                                    {tab.label}
                                  </h5>
                                </div>
                              </CardHeader>
                              <CardBody className='pt-0'>
                                <Row className='mb-3'>
                                  <Col xs={12}>
                                    <Row className='g-3'>
                                      <Col xs={12} md={6} lg={3}>
                                        <Label className='form-label'>
                                          Search
                                        </Label>
                                        <Input
                                          type='text'
                                          placeholder='Search by title or procurement no...'
                                          value={searchText}
                                          onChange={handleSearch}
                                          className='form-control'
                                        />
                                      </Col>
                                      <Col xs={12} md={6} lg={3}>
                                        <Label className='form-label'>
                                          Status
                                        </Label>
                                        <Input
                                          type='select'
                                          value={statusFilter}
                                          onChange={e =>
                                            handleStatusFilter(e.target.value)
                                          }
                                          className='form-control'
                                        >
                                          <option value=''>All Status</option>
                                          <option value='Open'>Open</option>
                                          <option value='Accepted'>
                                            Accepted
                                          </option>
                                          <option value='Declined'>
                                            Declined
                                          </option>
                                        </Input>
                                      </Col>
                                      <Col
                                        xs={12}
                                        md={6}
                                        lg={3}
                                        className='d-flex align-items-end'
                                      >
                                        <Button
                                          color='danger'
                                          outline
                                          onClick={clearFilters}
                                          className='w-100'
                                        >
                                          <i className='ri-refresh-line me-1'></i>
                                          <span className='d-none d-sm-inline'>
                                            Clear Filters
                                          </span>
                                          <span className='d-sm-none'>
                                            Clear
                                          </span>
                                        </Button>
                                      </Col>
                                    </Row>
                                  </Col>
                                </Row>

                                <Row className='mb-2'>
                                  <Col xs={12}>
                                    <small className='text-muted'>
                                      {isLoading
                                        ? 'Loading...'
                                        : `Showing ${startEntry} to ${endEntry} of ${totalRows} entries`}
                                    </small>
                                  </Col>
                                </Row>

                                {isLoading ? (
                                  <div className='text-center py-5'>
                                    <Spinner color='primary' />
                                    <p className='text-muted small mt-2 mb-0'>
                                      Loading invitation data...
                                    </p>
                                  </div>
                                ) : (
                                  <>
                                    <div className='d-md-none mt-3'>
                                      {data.items?.length > 0 ? (
                                        data.items.map((item, index) => (
                                          <MobileCard
                                            key={item.id || index}
                                            item={item}
                                          />
                                        ))
                                      ) : (
                                        <div className='table-empty text-center py-4'>
                                          <i
                                            className='ri-inbox-line text-muted'
                                            style={{
                                              fontSize: '3rem',
                                              opacity: 0.5,
                                            }}
                                          ></i>
                                          <p className='mt-2 text-muted'>
                                            No data found
                                          </p>
                                        </div>
                                      )}
                                    </div>

                                    <div className='d-none d-md-block'>
                                      <div
                                        className='table-responsive'
                                        style={{
                                          maxHeight: '600px',
                                          overflowY: 'auto',
                                        }}
                                      >
                                        <DataTable
                                          columns={columns}
                                          data={data.items}
                                          pagination={false}
                                          onSort={handleSort}
                                          sortServer
                                          defaultSortFieldId='procurementNumber'
                                          defaultSortAsc={false}
                                          highlightOnHover
                                          responsive
                                          striped
                                          persistTableHead
                                          dense
                                          customStyles={TABLE_CUSTOM_STYLES}
                                          noDataComponent={
                                            <div className='table-empty text-center py-4'>
                                              <i
                                                className='ri-inbox-line text-muted'
                                                style={{
                                                  fontSize: '3rem',
                                                  opacity: 0.5,
                                                }}
                                              ></i>
                                              <p className='mt-2 text-muted'>
                                                No data found
                                              </p>
                                            </div>
                                          }
                                        />
                                      </div>
                                    </div>
                                  </>
                                )}

                                <div className='d-flex justify-content-between align-items-center mt-3 px-3 py-2 border-top'>
                                  <div className='d-flex align-items-center'>
                                    <span className='text-muted me-2'>
                                      Rows per page:
                                    </span>
                                    <select
                                      className='form-select form-select-sm'
                                      style={{ width: 'auto' }}
                                      value={rowsPerPage}
                                      onChange={e =>
                                        handlePerRowsChange(
                                          parseInt(e.target.value, 10)
                                        )
                                      }
                                    >
                                      <option value={10}>10</option>
                                      <option value={25}>25</option>
                                      <option value={50}>50</option>
                                      <option value={100}>100</option>
                                    </select>
                                  </div>

                                  <div className='d-flex align-items-center'>
                                    <span className='text-muted me-3'>
                                      {startEntry}-{endEntry} of {totalRows}
                                    </span>
                                    <div className='btn-group' role='group'>
                                      <button
                                        type='button'
                                        className='btn btn-outline-secondary btn-sm'
                                        disabled={currentPage <= 1 || isLoading}
                                        onClick={() => handlePageChange(1)}
                                      >
                                        <i className='ri-skip-back-line'></i>
                                      </button>
                                      <button
                                        type='button'
                                        className='btn btn-outline-secondary btn-sm'
                                        disabled={currentPage <= 1 || isLoading}
                                        onClick={() =>
                                          handlePageChange(currentPage - 1)
                                        }
                                      >
                                        <i className='ri-arrow-left-s-line'></i>
                                      </button>
                                      <span className='btn btn-outline-secondary btn-sm disabled'>
                                        {currentPage}
                                      </span>
                                      <button
                                        type='button'
                                        className='btn btn-outline-secondary btn-sm'
                                        disabled={
                                          currentPage >= totalPages || isLoading
                                        }
                                        onClick={() =>
                                          handlePageChange(currentPage + 1)
                                        }
                                      >
                                        <i className='ri-arrow-right-s-line'></i>
                                      </button>
                                      <button
                                        type='button'
                                        className='btn btn-outline-secondary btn-sm'
                                        disabled={
                                          currentPage >= totalPages || isLoading
                                        }
                                        onClick={() =>
                                          handlePageChange(totalPages)
                                        }
                                      >
                                        <i className='ri-skip-forward-line'></i>
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                <ToastContainer closeButton={false} limit={1} />
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </TabPane>
                    ))}
                  </TabContent>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default InvitationView;
